import React, { useMemo, useState } from 'react';
import { Briefcase, Users, ChevronDown, ChevronUp, Maximize2, Minimize2, Layers, Building2, Network, MapPin, User } from 'lucide-react';

const TIPO_LABELS = {
  division: 'División', sucursal: 'Sucursal', sede: 'Sede',
  region: 'Región', area: 'Área', holding: 'Holding', unidad: 'Unidad',
};

// ── Posición compacta (indentada) dentro de una tarjeta de dept ──
function PositionRow({ position, level = 0, allPositions, onPositionClick }) {
  const children = allPositions.filter(p => p.reportaA === position.id);
  const [open, setOpen] = useState(true);

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-gray-100 cursor-pointer group transition-colors"
        style={{ paddingLeft: `${8 + level * 16}px` }}
        onClick={() => onPositionClick && onPositionClick(position)}
      >
        {/* Indicador de nivel */}
        {level > 0 && (
          <span className="text-gray-300 text-xs select-none">└</span>
        )}
        <Briefcase className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-700 font-medium flex-1 truncate group-hover:text-blue-600">
          {position.nombre}
        </span>
        {children.length > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
            className="p-0.5 rounded text-gray-300 hover:text-gray-500"
          >
            {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>
      {open && children.map(child => (
        <PositionRow
          key={child.id}
          position={child}
          level={level + 1}
          allPositions={allPositions}
          onPositionClick={onPositionClick}
        />
      ))}
    </div>
  );
}

// ── Tarjeta de Departamento ──────────────────────────────────────
function DepartmentCard({ department, positions, onPositionClick }) {
  const deptPositions = positions.filter(p => p.departamentoId === department.id);
  // Top-level dentro de este dept: sin reportaA, o reportaA apunta a otro dept
  const topLevel = deptPositions.filter(
    p => !p.reportaA || !deptPositions.find(dp => dp.id === p.reportaA)
  );
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex-shrink-0 w-64 min-w-[220px]">
      {/* Header dept */}
      <div
        className="h-1.5 rounded-t-xl"
        style={{ backgroundColor: department.color }}
      />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-2 h-2 rounded-full flex-shrink-0 mt-1"
              style={{ backgroundColor: department.color }}
            />
            <h4 className="text-sm font-bold text-gray-800 leading-snug">
              {department.nombre}
            </h4>
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-300 hover:text-gray-500 flex-shrink-0"
            title={collapsed ? 'Expandir' : 'Contraer'}
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>

        {/* Meta del dept */}
        <div className="space-y-1 mb-3">
          {department.responsable && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <User className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{department.responsable}</span>
            </div>
          )}
          {department.ubicacion && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{department.ubicacion}</span>
            </div>
          )}
        </div>

        {/* Badge conteo */}
        <div className="flex items-center gap-1.5 mb-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
            <Briefcase className="w-3 h-3" />
            {deptPositions.length} cargo{deptPositions.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Árbol de posiciones */}
        {!collapsed && (
          <div className="border-t border-gray-100 pt-2">
            {topLevel.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-1 px-2">Sin cargos asignados</p>
            ) : (
              <div className="space-y-0.5">
                {topLevel.map(pos => (
                  <PositionRow
                    key={pos.id}
                    position={pos}
                    level={0}
                    allPositions={deptPositions}
                    onPositionClick={onPositionClick}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sección de Unidad Organizativa ──────────────────────────────
function UnitSection({ unit, departments, positions, onPositionClick }) {
  const unitDepts = departments.filter(d => d.organizational_unit_id === unit.id);
  const totalPositions = positions.filter(
    p => unitDepts.some(d => d.id === p.departamentoId)
  ).length;
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: unit.color + '40' }}>
      {/* Header de la unidad */}
      <div
        className="px-6 py-4 flex items-center justify-between"
        style={{ backgroundColor: unit.color + '15' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm"
            style={{ backgroundColor: unit.color }}
          >
            {unit.nombre[0].toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-800">{unit.nombre}</h3>
              <span
                className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: unit.color }}
              >
                {TIPO_LABELS[unit.tipo] || unit.tipo}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {unitDepts.length} departamento{unitDepts.length !== 1 ? 's' : ''} · {totalPositions} cargo{totalPositions !== 1 ? 's' : ''}
              {unit.responsable && ` · ${unit.responsable}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 rounded-lg text-gray-400 hover:bg-white/60 hover:text-gray-600 transition-colors"
        >
          {collapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
        </button>
      </div>

      {/* Departamentos */}
      {!collapsed && (
        <div className="p-4 bg-white">
          {unitDepts.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">
              <Building2 className="w-8 h-8 text-gray-200 mx-auto mb-2" />
              Sin departamentos asignados
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {unitDepts.map(dept => (
                <DepartmentCard
                  key={dept.id}
                  department={dept}
                  positions={positions}
                  onPositionClick={onPositionClick}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════════
export default function OrgChart({ departments, positions, orgUnits = [], onPositionClick }) {
  const [fullscreen, setFullscreen] = useState(false);
  // Vista: 'structural' (units→depts→cargos) o 'hierarchy' (árbol reportaA original)
  const [chartView, setChartView] = useState('hierarchy');

  // ── Vista jerárquica (árbol reportaA — vista original) ──────────
  const [expandedPositions, setExpandedPositions] = useState(new Set());
  const [expandAll, setExpandAll] = useState(true);

  React.useEffect(() => {
    if (expandAll && positions.length > 0) {
      setExpandedPositions(new Set(positions.map(p => p.id)));
    }
  }, [positions, expandAll]);

  const orgStructure = useMemo(() => {
    const topLevel = positions.filter(p => !p.reportaA);
    const buildTree = (parentId, level = 0) =>
      positions
        .filter(p => p.reportaA === parentId)
        .map(p => ({ ...p, level, children: buildTree(p.id, level + 1) }));
    return topLevel.map(p => ({ ...p, level: 0, children: buildTree(p.id, 1) }));
  }, [positions]);

  const togglePosition = (id) => {
    setExpandedPositions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const getDepartmentColor = (deptId) => departments.find(d => d.id === deptId)?.color || '#667eea';
  const getDepartmentName  = (deptId) => departments.find(d => d.id === deptId)?.nombre || 'Sin departamento';

  // ── Datos para la vista estructural ─────────────────────────────
  const orphanDepts = useMemo(
    () => departments.filter(d => !d.organizational_unit_id || !orgUnits.find(u => u.id === d.organizational_unit_id)),
    [departments, orgUnits]
  );

  // ── Nodo de posición para la vista jerárquica ────────────────────
  const HierarchyNode = ({ position, level = 0 }) => {
    const hasChildren = position.children?.length > 0;
    const isExpanded  = expandedPositions.has(position.id);
    const color       = getDepartmentColor(position.departamentoId);

    return (
      <div className="flex flex-col items-center relative">
        {level > 0 && (
          <div className="w-0.5 h-8 absolute -top-8 left-1/2 -translate-x-1/2" style={{ backgroundColor: color + '60' }} />
        )}
        <div className="relative group z-10">
          <div
            className="bg-white rounded-xl border border-gray-200 border-l-4 p-4 min-w-[260px] max-w-[260px] cursor-pointer hover:shadow-md transition-shadow"
            style={{ borderLeftColor: color }}
            onClick={() => onPositionClick && onPositionClick(position)}
          >
            <div className="mb-2">
              <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: color }}>
                {getDepartmentName(position.departamentoId)}
              </span>
            </div>
            <div className="flex items-start gap-2 mb-2">
              <Briefcase className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color }} />
              <h4 className="font-bold text-gray-800 text-sm leading-tight">{position.nombre}</h4>
            </div>
            {position.proposito && (
              <p className="text-xs text-gray-500 line-clamp-2">{position.proposito}</p>
            )}
            <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: color }}>
              {level}
            </div>
            {hasChildren && (
              <button
                onClick={(e) => { e.stopPropagation(); togglePosition(position.id); }}
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white border-2 rounded-full p-1 shadow-md z-20"
                style={{ borderColor: color }}
              >
                {isExpanded ? <ChevronUp className="w-3 h-3" style={{ color }} /> : <ChevronDown className="w-3 h-3" style={{ color }} />}
              </button>
            )}
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="flex flex-col items-center w-full mt-8">
            <div className="w-0.5 h-8" style={{ backgroundColor: color + '60' }} />
            <div className="relative">
              {position.children.length > 1 && (
                <div className="absolute h-0.5 top-0" style={{ backgroundColor: color + '60', left: '50%', transform: 'translateX(-50%)', width: `calc(${(position.children.length - 1) * 100}%)` }} />
              )}
              <div className="flex justify-center items-start" style={{ gap: '3rem' }}>
                {position.children.map((child, idx) => (
                  <div key={child.id} className="relative flex flex-col items-center">
                    <div className="w-0.5 h-8 mb-2" style={{ backgroundColor: color + '60' }} />
                    <HierarchyNode position={child} level={level + 1} isLast={idx === position.children.length - 1} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (positions.length === 0 && departments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <Network className="w-16 h-16 text-gray-200 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-500 mb-2">No hay estructura definida</h3>
        <p className="text-gray-400 text-sm">Crea unidades, departamentos y cargos para visualizar el organigrama</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${fullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>
      {/* ── Header con controles ────────────────────────────────────── */}
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-100">
            <Network className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Organigrama</h3>
            <p className="text-xs text-gray-500">
              {departments.length} dept{departments.length !== 1 ? 's' : ''} · {positions.length} cargo{positions.length !== 1 ? 's' : ''}
              {orgUnits.length > 0 && ` · ${orgUnits.length} unidad${orgUnits.length !== 1 ? 'es' : ''}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Toggle vista */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setChartView('hierarchy')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${
                chartView === 'hierarchy' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Network className="w-3.5 h-3.5" />
              Jerárquica
            </button>
            <button
              onClick={() => setChartView('structural')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors border-l border-gray-200 ${
                chartView === 'structural' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              Estructural
            </button>
          </div>

          {chartView === 'hierarchy' && (
            <button
              onClick={() => {
                const next = !expandAll;
                setExpandAll(next);
                setExpandedPositions(next ? new Set(positions.map(p => p.id)) : new Set());
              }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg flex items-center gap-1.5 hover:bg-blue-700 transition-colors text-xs font-semibold"
            >
              {expandAll ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {expandAll ? 'Contraer' : 'Expandir'}
            </button>
          )}

          <button
            onClick={() => setFullscreen(!fullscreen)}
            className="px-3 py-1.5 bg-gray-700 text-white rounded-lg flex items-center gap-1.5 hover:bg-gray-800 transition-colors text-xs font-semibold"
          >
            {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            {fullscreen ? 'Salir' : 'Pantalla completa'}
          </button>
        </div>
      </div>

      {/* ── Vista Estructural: Unidades → Departamentos → Cargos ────── */}
      {chartView === 'structural' && (
        <div className="p-5 space-y-5 overflow-x-auto">
          {/* Unidades con sus departamentos */}
          {orgUnits.map(unit => (
            <UnitSection
              key={unit.id}
              unit={unit}
              departments={departments}
              positions={positions}
              onPositionClick={onPositionClick}
            />
          ))}

          {/* Departamentos sin unidad asignada */}
          {orphanDepts.length > 0 && (
            <div className="rounded-2xl border-2 border-dashed border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-600">Sin unidad organizativa</h3>
                    <p className="text-xs text-gray-400">{orphanDepts.length} departamento{orphanDepts.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-white">
                <div className="flex flex-wrap gap-4">
                  {orphanDepts.map(dept => (
                    <DepartmentCard
                      key={dept.id}
                      department={dept}
                      positions={positions}
                      onPositionClick={onPositionClick}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Si no hay unidades ni depts: solo depts */}
          {orgUnits.length === 0 && departments.length > 0 && (
            <div className="flex flex-wrap gap-4">
              {departments.map(dept => (
                <DepartmentCard
                  key={dept.id}
                  department={dept}
                  positions={positions}
                  onPositionClick={onPositionClick}
                />
              ))}
            </div>
          )}

          {/* Pie: instrucción */}
          <p className="text-xs text-gray-400 text-center pt-2">
            Haz clic en cualquier cargo para ver o editar sus detalles
          </p>
        </div>
      )}

      {/* ── Vista Jerárquica: árbol por reportaA ────────────────────── */}
      {chartView === 'hierarchy' && (
        <div>
          {/* Leyenda de departamentos */}
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="flex flex-wrap gap-2">
              {departments.map(dept => (
                <div key={dept.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 bg-white text-sm" style={{ borderColor: dept.color }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />
                  <span className="font-semibold text-gray-700">{dept.nombre}</span>
                  <span className="text-gray-400">({positions.filter(p => p.departamentoId === dept.id).length})</span>
                </div>
              ))}
            </div>
          </div>

          {orgStructure.length === 0 ? (
            <div className="py-16 text-center">
              <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500 mb-1">No hay jerarquía definida</p>
              <p className="text-xs text-gray-400">Asigna el campo "Reporta a" en los cargos para construir el árbol jerárquico</p>
            </div>
          ) : (
            <div className="overflow-x-auto pb-8">
              <div className="min-w-max p-8 flex flex-col items-center gap-16">
                {orgStructure.map((pos, idx) => (
                  <div key={pos.id} className="flex flex-col items-center">
                    {idx > 0 && <div className="h-12 w-0.5 bg-gray-300 mb-4" />}
                    <HierarchyNode position={pos} level={0} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
